import { useMemo, useState } from "react"
import { Box, Stack, Divider, Tooltip } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { EventImpl as ez } from "@fullcalendar/core/internal"
import { useDateTime } from "../../../modules/localization/useDateTime"
import {
    AdditionalEventMenuItem,
    MeetingCardMenu,
} from "../../../modules/nonAdminUser/reports/home/components/upcomingOrders/meetings/MeetingCardMenu"
import { faCalendarCheck, faMugSaucer, faLocationDot } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { MenuMeeting } from "../../../modules/nonAdminUser/reports/home/weekMenuTypes"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import { faClock, faUserGroup } from "@fortawesome/pro-light-svg-icons"
import { resolveEventColors } from "./utils"
import { CalendarEvent } from "./calendarTypes"

type StyleProps = Readonly<{
    apiColor: string | undefined
    apiBgColor: string | undefined
}>

const useStyles = makeStyles<StyleProps>()((theme, { apiColor, apiBgColor }) => {
    const { color, bgColor } = resolveEventColors(theme, apiColor, apiBgColor)

    return {
        dayContent: {
            position: "relative",
            width: "100%",
            padding: ".25rem .5rem .25rem .25rem",
            borderRadius: ".25rem",
            overflow: "hidden",
            borderLeft: "4px solid",
            backgroundColor: bgColor,
            color,
        },
        eventTitle: {
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
        },
        event: {
            cursor: "pointer",
            paddingLeft: "2px !important",
            minHeight: "2.75rem",
        },
        moreMenu: {
            position: "absolute",
            bottom: "0rem",
            right: "0rem",
        },
        meetingTitle: {
            color,
            fontWeight: 500,
            fontSize: ".75rem",
        },
        meetingTime: {
            color,
            fontSize: ".75rem !important",
        },
        meetingDetailsIcon: {
            color: color,
        },
        meetingServiceIcon: {
            color: color,
        },
        iconWrapper: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: ".5rem",
            width: "1rem",
        },
        location: {
            color,
            fontSize: ".75rem !important",
        },
        participantsContainer: {
            color,
            fontSize: ".75rem !important",
            marginLeft: ".25rem",
        },
        meetingIconContainer: {
            padding: "0.25rem 0",
        },
        eventHeaderContent: {
            backgroundColor: tailwindPalette.slate[50],
            fontWeight: 500,
            padding: "0.5rem 1rem",
            fontSize: ".75rem",
        },
        eventTime: {
            fontWeight: 500,
            fontSize: ".75rem",
        },
        menuEventTitle: {
            fontSize: "1rem",
            maxWidth: "10rem",
        },
        menuEventTime: {
            fontSize: "1rem",
        },
    }
})

export const DayGridContent = ({
    event,
    additionalMenuItems,
    events,
    onChatOpen,
}: {
    additionalMenuItems?: AdditionalEventMenuItem[]
    event: ez
    events: CalendarEvent[]
    onChatOpen?: (orderId: number, orderTitle: string) => void
}) => {
    const { classes, cx } = useStyles({
        apiColor: event.extendedProps["color"],
        apiBgColor: event.extendedProps["bgColor"],
    })
    const dateTimeFactory = useDateTime()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const start = dateTimeFactory.fromApi(event.startStr)
    const end = dateTimeFactory.fromApi(event.endStr)
    const { hasBooking, hasCatering } = event.extendedProps["orderSummary"] ?? {}

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const onEventClick = (event: React.MouseEvent<HTMLElement>) => {
        if (anchorEl) {
            setAnchorEl(null)
        } else {
            setAnchorEl(event.currentTarget)
        }
    }

    const menuMeeting = useMemo((): MenuMeeting => {
        return {
            id: +event.id,
            uid: event.extendedProps["uid"],
            orderSummary: event.extendedProps["orderSummary"],
            organizers: event.extendedProps["organizers"],
            meetingDetails: {
                meetingName: event.title,
                startDateTime: event.startStr,
                endDateTime: event.endStr,
            },
        }
    }, [event])

    const topComponent = useMemo(() => {
        if (event.startStr && event.endStr)
            return (
                <Stack className={classes.eventHeaderContent} gap={0.5}>
                    <Tooltip title={event.title} arrow>
                        <Box className={`fc-sticky ${cx(classes.eventTitle, classes.menuEventTitle)}`}>
                            {event.title}
                        </Box>
                    </Tooltip>
                    <Stack direction="row" gap={0.5} alignItems="center">
                        <Box className={cx(classes.iconWrapper)}>
                            <FontAwesomeIcon icon={faClock} />
                        </Box>
                        <Box className={cx(classes.eventTime, classes.menuEventTime)}>
                            {dateTimeFactory.formatTime(start)} - {dateTimeFactory.formatTime(end)}
                        </Box>
                    </Stack>
                </Stack>
            )

        return null
    }, [event.title, start, end])

    return (
        <Box className={cx(classes.dayContent)}>
            <Box className={`fc-event-main ${classes.event}`}>
                <Stack gap={0.5} onClick={onEventClick}>
                    {(hasCatering || hasBooking) && (
                        <Stack direction="row" gap={0.5} className={classes.meetingIconContainer}>
                            {hasBooking && (
                                <Box className={cx(classes.iconWrapper, classes.meetingDetailsIcon)}>
                                    <FontAwesomeIcon icon={faCalendarCheck} />
                                </Box>
                            )}
                            {hasCatering && (
                                <Box className={cx(classes.iconWrapper, classes.meetingServiceIcon)}>
                                    <FontAwesomeIcon icon={faMugSaucer} />
                                </Box>
                            )}
                        </Stack>
                    )}
                    <Stack direction="row" className={`fc-event-title-container ${classes.meetingTitle}`}>
                        <Tooltip title={event.title} arrow>
                            <Box className={`fc-sticky ${classes.eventTitle}`}>{event.title}</Box>
                        </Tooltip>
                        {!!event.extendedProps["participants"] && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                                className={classes.participantsContainer}
                            >
                                <Divider orientation="vertical" />
                                <Stack
                                    direction="row"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    gap={0.5}
                                    className={classes.location}
                                >
                                    <Box className={cx(classes.iconWrapper)}>
                                        <FontAwesomeIcon icon={faUserGroup} />
                                    </Box>
                                    <Box>{event.extendedProps["participants"]}</Box>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>

                    <Stack direction="row" gap={0.5} alignItems="center" className={classes.meetingTime}>
                        {event.allDay && !event.extendedProps["ignoreAllDayLabel"] && (
                            <Box className={classes.eventTime}>All day</Box>
                        )}
                        {event.startStr && event.endStr && (
                            <Box className={classes.eventTime}>
                                {dateTimeFactory.formatTime(start)} - {dateTimeFactory.formatTime(end)}
                            </Box>
                        )}
                    </Stack>
                    {event.extendedProps["location"] && (
                        <Stack direction="row" alignItems="center" gap={0.5} className={classes.location}>
                            <Box className={cx(classes.iconWrapper)}>
                                <FontAwesomeIcon icon={faLocationDot} />
                            </Box>
                            <Box>{event.extendedProps["location"]}</Box>
                        </Stack>
                    )}
                </Stack>

                <Box className={classes.moreMenu}>
                    {anchorEl && (
                        <MeetingCardMenu
                            additionalMenuItems={additionalMenuItems}
                            topComponent={topComponent}
                            meeting={menuMeeting}
                            anchorEl={anchorEl}
                            menuOnApi
                            orders={events ?? [menuMeeting]}
                            handleMenuClose={handleMenuClose}
                            onChatOpen={onChatOpen}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    )
}
